import { assertNever } from '@kofno/piper';
import * as React from 'react';
import { useState } from 'react';
import { T } from '../../../../../Translations';
import { Program } from '../Stores/ShareStore/Types';
import { startDate } from './WhenItStarts';

// Scheduled dates shown before the "+N more" chip. The row sits under the
// course title, after the "Start dates" label, and wraps under its first
// chip when the dates do not fit on one line. No course family in
// production has more than five runs open, so the common card shows every
// date with nothing to expand, and the overflow chip appears only on a rare
// six-plus course.
const SHOWN_BEFORE_MORE = 5;

const chipClassName =
  'inline-block whitespace-nowrap rounded border border-border bg-card px-1.5 py-0.5 text-foreground';

const isScheduled = (run: Program): boolean => {
  switch (run.kind) {
    case 'scheduled':
      return true;
    case 'on-demand':
      return false;
    default:
      return assertNever(run.kind);
  }
};

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className={chipClassName} style={{ fontSize: 'var(--text-xs)' }}>
      {children}
    </span>
  );
}

// DVLP-8060: the dates a shared URL offers, under the course on the card, so
// the admin sees what participants will choose from: a "Start anytime" chip
// when the family runs on demand, then its scheduled dates, capped with a
// "+N more" chip that expands the row in place and stays open (the dialog is
// short-lived). The row sits under the course title, inside the card's
// button, so the "+N more" chip keeps its click and keys to itself and
// expanding does not open the catalog. Changing which runs the URL offers is
// a later ticket.
function StartDateChips({ runs }: { runs: ReadonlyArray<Program> }) {
  const [open, setOpen] = useState(false);
  const onDemand = runs.some((run) => !isScheduled(run));
  const scheduled = runs.filter(isScheduled);
  const shown = open ? scheduled : scheduled.slice(0, SHOWN_BEFORE_MORE);
  const hidden = scheduled.length - shown.length;
  const chipCount = scheduled.length + (onDemand ? 1 : 0);

  return (
    <div className="flex items-baseline gap-2">
      <div
        className="flex-none text-foreground"
        style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)' }}
      >
        {chipCount === 1 ? <T kind="Start date" /> : <T kind="Start dates" />}
      </div>
      <div className="flex min-w-0 flex-1 flex-wrap gap-x-1.5 gap-y-1">
        {onDemand && (
          <Chip>
            <T kind="Start anytime" />
          </Chip>
        )}
        {shown.map((run) => (
          <Chip key={run.id}>{startDate(run.startOn)}</Chip>
        ))}
        {hidden > 0 && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(true);
            }}
            onKeyDown={(event) => event.stopPropagation()}
            aria-expanded={open}
            className={`${chipClassName} border-dashed text-primary hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring`}
            style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)' }}
            data-intercom-target="share-course-more-dates"
          >
            <T kind="+{{count}} more" count={hidden} />
          </button>
        )}
      </div>
    </div>
  );
}

export default StartDateChips;
